/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import {
    collection,
    getDocs,
    doc,
    getDoc,
    query,
    where,
    addDoc,
    serverTimestamp,
    orderBy,
    limit,
    onSnapshot,
    runTransaction,
    setDoc,
    updateDoc,
    increment
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { getISTDate, getMasterKey } from "../utils/dateUtils";
import BlankLayout from "../components/BlankLayout";


import { FaHistory, FaBoxes, FaTruckLoading, FaStore, FaDatabase, FaSignOutAlt, FaUserFriends, FaClipboardList, FaSearch, FaChevronRight, FaTimes, FaCheckDouble, FaEdit, FaPlus, FaLock } from "react-icons/fa";

const getIstDateStr = getISTDate;

function DistributorMobileDashboard() {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("cockpit");
    const [companyInfo, setCompanyInfo] = useState({});

    // Shared Cache
    const [skuMaster, setSkuMaster] = useState({});
    const [skuList, setSkuList] = useState([]);
    const [dsList, setDsList] = useState([]);
    const [outletList, setOutletList] = useState([]);

    // Adaptive Intelligence
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
    useEffect(() => {
        const checkSize = () => setIsDesktop(window.innerWidth > 1024);
        window.addEventListener('resize', checkSize);
        return () => window.removeEventListener('resize', checkSize);
    }, []);

    // Ultra-Optimized Cache (Incremental)
    const [sharedData, setSharedData] = useState({
        dsOrders: [],       // Last 3 days
        outletOrders: [],   // Last 3 days
        todayStock: null,
        aggregate: null,    // Monthly real-time counters
        isSyncing: true
    });

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    const snap = await getDoc(doc(db, "users", user.uid));
                    if (snap.exists()) {
                        const profile = snap.data();
                        setUserProfile(profile);
                        const distSnap = await getDoc(doc(db, "partyMaster", profile.partyId));
                        if (distSnap.exists()) {
                            const dData = distSnap.data();
                            setCompanyInfo({ 
                                id: profile.partyId, 
                                partyId: profile.partyId,
                                name: dData.name || dData.partyName || "Distributor HQ",
                                address1: dData.address1 || "",
                                phone: dData.phone || "",
                                gstin: dData.gstin || ""
                            });
                        }
                    }
                }
            } catch (err) {
                console.warn("Distributor profile fetch failed (likely offline):", err);
            } finally {
                setLoading(false);
            }
        });
        return () => unsub();
    }, [navigate]);

    // Optimized Data Management
    useEffect(() => {
        if (!userProfile?.partyId) return;

        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        threeDaysAgo.setHours(0,0,0,0);
        const todayStr = getIstDateStr();
        const monthId = todayStr.slice(0, 7); // "2026-03"

        // 1. Observer: Today's Stock (Lazy Load - only if needed for transactions or inventory tab)
        let unsubStock = null;
        if (["stock", "bill_ds", "bill_outlet"].includes(activeTab)) {
            unsubStock = onSnapshot(doc(db, "distributor_stock_daily", `${userProfile.partyId}_${todayStr}`), (s) => {
                setSharedData(prev => ({ ...prev, todayStock: s.exists() ? s.data() : null }));
            });
        }

        // 2. Observer: Monthly Real-Time Aggregate (Single Document for all KPI)
        const unsubAgg = onSnapshot(doc(db, "distributor_aggregates", `${userProfile.partyId}_${monthId}`), (s) => {
            setSharedData(prev => ({ ...prev, aggregate: s.exists() ? s.data() : null }));
        });

        // 3 & 4. Observers: Recent Activity (Only for Reports Tab)
        let unsubDs = null, unsubOut = null;
        if (activeTab === "report") {
            const qDs = query(
                collection(db, "ds_orders"), 
                where("clientId", "==", userProfile.clientId),
                where("distributorId", "==", userProfile.partyId), 
                where("createdAt", ">=", threeDaysAgo)
            );
            unsubDs = onSnapshot(qDs, (snap) => {
                const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setSharedData(prev => ({ ...prev, dsOrders: data }));
            });

            const qOut = query(
                collection(db, "Outlet_Orders"), 
                where("clientId", "==", userProfile.clientId),
                where("distributorId", "==", userProfile.partyId), 
                where("createdAt", ">=", threeDaysAgo)
            );
            unsubOut = onSnapshot(qOut, (snap) => {
                const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setSharedData(prev => ({ ...prev, outletOrders: data, isSyncing: false }));
            });
        } else {
            // Signal sync as done for non-report tabs if shared data aggregate exists
            setSharedData(prev => ({ ...prev, isSyncing: false }));
        }

        const loadMasters = async () => {
             // 1. Load SKUs with Fallback
             let skuQ = query(collection(db, "skuMaster"), where("clientId", "==", userProfile.clientId));
             let skuSnap = await getDocs(skuQ);
             if (skuSnap.empty) {
                 skuQ = query(collection(db, "skuMaster"));
                 skuSnap = await getDocs(skuQ);
             }

             // 2. Load DS Master with Fallback
             let dsQ = query(collection(db, "dsMaster"), where("clientId", "==", userProfile.clientId), where("distributorId", "==", userProfile.partyId), where("status", "==", "active"));
             let dsSnap = await getDocs(dsQ);
             if (dsSnap.empty) {
                 dsQ = query(collection(db, "dsMaster"), where("distributorId", "==", userProfile.partyId), where("status", "==", "active"));
                 dsSnap = await getDocs(dsQ);
             }

             // 3. Load Outlet Master with Fallback
             let outQ = query(collection(db, "outletMaster"), where("clientId", "==", userProfile.clientId), where("distributorId", "==", userProfile.partyId));
             let outSnap = await getDocs(outQ);
             if (outSnap.empty) {
                 outQ = query(collection(db, "outletMaster"), where("distributorId", "==", userProfile.partyId));
                 outSnap = await getDocs(outQ);
             }

             const smap = {}; const slist = [];
             skuSnap.forEach(d => {
                 const data = d.data(); const key = String(data.sku || data.Name || "").toUpperCase();
                 smap[key] = data; slist.push(key);
             });
             slist.sort((a,b) => (Number(smap[a].sortorder) || 999) - (Number(smap[b].sortorder) || 999));
             setSkuMaster(smap); setSkuList(slist);
             setDsList(dsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
             setOutletList(outSnap.docs.map(d => ({ id: d.id, ...d.data() })));
         };

         if (["bill_ds", "bill_outlet", "master", "stock"].includes(activeTab)) {
            loadMasters();
         }
        return () => { 
            if (unsubStock) unsubStock(); 
            if (unsubDs) unsubDs();
            if (unsubOut) unsubOut();
            unsubAgg(); 
        };
    }, [userProfile, activeTab]);

    if (loading) return <div style={loaderStyle}>⚡ Establishing Secure Uplink...</div>;

    const handleLogout = () => auth.signOut();

    const navProps = { activeTab, setActiveTab, userProfile, companyInfo, sharedData, skuMaster, skuList, dsList, outletList, handleLogout, isDesktop };

    if (isDesktop) return <DistributorDesktopView {...navProps} />;

    return (
        <BlankLayout>
            <div style={container}>
                {/* Header with Logout */}
                <div style={header}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={pulse}></div>
                            <h1 style={{ fontSize: 16, fontWeight: 900, color: "white", margin: 0, letterSpacing: 1 }}>DISTRIBUTOR MOBILE</h1>
                        </div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{userProfile?.partyId || "Syncing..."} • {companyInfo?.name || "Loading..."}</div>
                    </div>
                    <button onClick={handleLogout} style={logoutBtn}><FaSignOutAlt /></button>
                </div>

                {/* Content Area */}
                <div style={content}>
                    {activeTab === "cockpit" && <CockpitModule dsList={dsList} skuList={skuList} userProfile={userProfile} sharedData={sharedData} isDesktop={isDesktop} />}
                    {activeTab === "bill_ds" && <BillDsModule dsList={dsList} skuMaster={skuMaster} skuList={skuList} userProfile={userProfile} companyInfo={companyInfo} isDesktop={isDesktop} />}
                    {activeTab === "bill_outlet" && <BillOutletModule dsList={dsList} outletList={outletList} skuMaster={skuMaster} skuList={skuList} userProfile={userProfile} companyInfo={companyInfo} isDesktop={isDesktop} />}
                    {activeTab === "master" && <MasterModule dsList={dsList} outletList={outletList} userProfile={userProfile} companyInfo={companyInfo} isDesktop={isDesktop} />}
                    {activeTab === "stock" && <StockLedgerModule userProfile={userProfile} skuMaster={skuMaster} skuList={skuList} sharedData={sharedData} isDesktop={isDesktop} />}
                    {activeTab === "report" && <SalesReportModule userProfile={userProfile} sharedData={sharedData} isDesktop={isDesktop} />}
                </div>

                {/* Bottom Nav */}
                <div style={bottomNav}>
                    <div style={navItem(activeTab === "cockpit")} onClick={() => setActiveTab("cockpit")}>
                        <FaHistory />
                        <span>Hub</span>
                    </div>
                    <div style={navItem(activeTab === "bill_ds")} onClick={() => setActiveTab("bill_ds")}>
                        <FaTruckLoading />
                        <span>Bill DS</span>
                    </div>
                    <div style={navItem(activeTab === "bill_outlet")} onClick={() => setActiveTab("bill_outlet")}>
                        <FaStore />
                        <span>Bill Outlet</span>
                    </div>
                    <div style={navItem(activeTab === "master")} onClick={() => setActiveTab("master")}>
                        <FaDatabase />
                        <span>Master</span>
                    </div>
                    <div style={navItem(activeTab === "stock")} onClick={() => setActiveTab("stock")}>
                        <FaBoxes />
                        <span>Stock</span>
                    </div>
                    <div style={navItem(activeTab === "report")} onClick={() => setActiveTab("report")}>
                        <FaClipboardList />
                        <span>Reports</span>
                    </div>
                </div>
            </div>
        </BlankLayout>
    );
}

/* --- LOGIC UTILS --- */
const buildItemLines = (items, skuMaster) => {
    const todayStr = getIstDateStr();
    return Object.entries(items).filter(([_, qty]) => Number(qty) > 0).map(([sku, qty]) => {
        const m = skuMaster[sku] || {};
        const basicRate = Number(m.dsBasicRate || m.Basic_Eway || 0);
        const gstP = Number(m.gst) || 0;
        const mrp = Number(m.mrp) || 0;
        const lineBasic = qty * basicRate;
        const lineTax = lineBasic * (gstP / 100);
        return {
            sku: sku,
            skuName: m.Name || sku,
            qty: qty,
            basicRate: basicRate,
            basicAmount: lineBasic,
            cgstRate: gstP / 2,
            sgstRate: gstP / 2,
            cgstAmount: lineTax / 2,
            sgstAmount: lineTax / 2,
            totalAmount: lineBasic + lineTax,
            hsn: m.hsn || "",
            mrp: mrp,
            orderDate: todayStr
        };
    });
};

const updateIncrementalAggregates = (transaction, partyId, amt, qty, dsId, outletId, currentInventory) => {
    const today = getIstDateStr();
    const monthId = today.slice(0, 7);
    const aggRef = doc(db, "distributor_aggregates", `${partyId}_${monthId}`);
    
    const update = {
        mtdSalesAmount: increment(amt || 0),
        mtdSalesUnits: increment(qty || 0),
        mtdStockSoldUnits: increment(qty || 0),
        [`daily.${today}.salesAmount`]: increment(amt || 0),
        [`daily.${today}.units`]: increment(qty || 0)
    };

    // If aggregator doesn't have opening units, calculate from current inventory snapshot
    if (currentInventory) {
        let opTotal = 0;
        Object.values(currentInventory.inventory || {}).forEach(item => {
            opTotal += (Number(item.opening) || 0);
        });
        update.mtdStockOpeningUnits = opTotal;
    }

    if (dsId) update[`daily.${today}.dsBilled.${dsId}`] = true;
    if (outletId) {
        update[`daily.${today}.outletBilled.${outletId}`] = true;
        update[`uniqueOutlets.${outletId}`] = true;
    }

    transaction.set(aggRef, update, { merge: true });
};

/* --- TAB 1: BILL TO DS --- */
function BillDsModule({ dsList, skuMaster, skuList, userProfile, companyInfo, isDesktop }) {
    const [selectedDs, setSelectedDs] = useState("");
    const [items, setItems] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState("");
    const [showPreview, setShowPreview] = useState(false);

    const totals = useMemo(() => {
        let qty = 0, amt = 0, basic = 0;
        Object.entries(items).forEach(([sku, q]) => {
            if (q > 0) {
                const m = skuMaster[sku];
                const rate = Number(m?.dsBasicRate || m?.Basic_Eway || 0);
                const gst = Number(m?.gst || 5);
                const lineBasic = q * rate;
                const lineTotal = lineBasic * (1 + gst / 100);
                qty += q;
                basic += lineBasic;
                amt += lineTotal;
            }
        });
        return { qty, amt, basic };
    }, [items, skuMaster]);

    const getCustomDocId = async (transaction, type) => {
        const todayYYMMDD = new Date().toISOString().slice(2, 10).replace(/-/g, ""); // "240317"
        const prefix = (companyInfo.name || "DS").slice(0, 2).toUpperCase();
        const counterId = `${userProfile.partyId}_${todayYYMMDD}_${type}`;
        const counterRef = doc(db, "counters", counterId);
        const counterSnap = await transaction.get(counterRef);
        let seq = 1;
        if (counterSnap.exists()) {
            seq = (counterSnap.data().seq || 0) + 1;
        }
        transaction.set(counterRef, { seq }, { merge: true });
        const serial = String(seq).padStart(4, "0");
        const displayId = `${prefix}/${todayYYMMDD}/${type}/${serial}`;
        const dbId = displayId.replace(/\//g, "-");
        return { dbId, displayId };
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            const dsObj = dsList.find(d => d.dsId === selectedDs);
            const itemLines = buildItemLines(items, skuMaster);
            const todayStr = getIstDateStr();
            
            // Strategy: Try Transaction (Online), Fallback to Atomic Writes (Offline)
            const saveOffline = async () => {
                const tempId = Date.now().toString().slice(-6);
                finalId = `OFF-DS-${tempId}`;
                const dbId = finalId.replace(/\//g, "-");
                const orderRef = doc(db, "orders", dbId);
                const orderData = {
                    clientId: userProfile.clientId,
                    orderId: dbId,
                    displayOrderId: finalId,
                    distributorId: userProfile.partyId,
                    distributorName: companyInfo.name,
                    partyId: selectedDs,
                    partyName: dsObj?.name || selectedDs,
                    items: items,
                    itemLines: itemLines,
                    totalQty: totals.qty,
                    totalAmount: totals.amt,
                    totalBasic: totals.basic,
                    cgst: (totals.amt - totals.basic) / 2,
                    sgst: (totals.amt - totals.basic) / 2,
                    source: "DISTRIBUTOR_TO_DS",
                    status: "PENDING_SYNC",
                    approvedAt: serverTimestamp(),
                    createdAt: serverTimestamp(),
                };
                setDoc(orderRef, orderData);
                const dsOrderRef = doc(db, "ds_orders", dbId);
                setDoc(dsOrderRef, { ...orderData, type: "DS_LOAD_AUTHORIZED" });

                const distStockRef = doc(db, "distributor_stock_daily", `${userProfile.partyId}_${todayStr}`);
                setDoc(distStockRef, { distributorId: userProfile.partyId, date: todayStr }, { merge: true });
                const distUpdate = {};
                Object.entries(items).forEach(([sku, qty]) => { if (qty > 0) distUpdate[`inventory.${sku}.sold`] = increment(qty); });
                updateDoc(distStockRef, distUpdate);

                const dsStockRef = doc(db, "ds_stock_daily", `${selectedDs}_${todayStr}`);
                setDoc(dsStockRef, { dsId: selectedDs, date: todayStr, initialized: true }, { merge: true });
                const dsUpdate = {};
                Object.entries(items).forEach(([sku, qty]) => { if (qty > 0) dsUpdate[`inventory.${sku}.billed`] = increment(qty); });
                updateDoc(dsStockRef, dsUpdate);
            };

            if (!navigator.onLine) {
                await saveOffline();
            } else {
                try {
                    await Promise.race([
                        runTransaction(db, async (transaction) => {
                            const { dbId, displayId } = await getCustomDocId(transaction, "DS");
                            finalId = displayId;
                            const orderRef = doc(db, "orders", dbId);
                            const orderData = {
                                clientId: userProfile.clientId,
                                orderId: dbId,
                                displayOrderId: displayId,
                                distributorId: userProfile.partyId,
                                distributorName: companyInfo.name,
                                partyId: selectedDs,
                                partyName: dsObj?.name || selectedDs,
                                items: items,
                                itemLines: itemLines,
                                totalQty: totals.qty,
                                totalAmount: totals.amt,
                                totalBasic: totals.basic,
                                cgst: (totals.amt - totals.basic) / 2,
                                sgst: (totals.amt - totals.basic) / 2,
                                source: "DISTRIBUTOR_TO_DS",
                                status: "APPROVED",
                                approvedAt: serverTimestamp(),
                                createdAt: serverTimestamp(),
                            };
                            transaction.set(orderRef, orderData);
                            const dsOrderRef = doc(db, "ds_orders", dbId);
                            transaction.set(dsOrderRef, { ...orderData, type: "DS_LOAD_AUTHORIZED" });
                            const distStockRef = doc(db, "distributor_stock_daily", `${userProfile.partyId}_${todayStr}`);
                            transaction.set(distStockRef, { distributorId: userProfile.partyId, date: todayStr }, { merge: true });
                            const distUpdate = {};
                            Object.entries(items).forEach(([sku, qty]) => { if (qty > 0) distUpdate[`inventory.${sku}.sold`] = increment(qty); });
                            transaction.update(distStockRef, distUpdate);
                            const dsStockRef = doc(db, "ds_stock_daily", `${selectedDs}_${todayStr}`);
                            transaction.set(dsStockRef, { dsId: selectedDs, date: todayStr, initialized: true }, { merge: true });
                            const dsUpdate = {};
                            Object.entries(items).forEach(([sku, qty]) => { if (qty > 0) dsUpdate[`inventory.${sku}.billed`] = increment(qty); });
                            transaction.update(dsStockRef, dsUpdate);
                            updateIncrementalAggregates(transaction, userProfile.partyId, totals.amt, totals.qty, selectedDs, null, sharedData.todayStock);
                        }),
                        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 2000))
                    ]);
                } catch (tErr) {
                    console.warn("Distributor Sync Slow, using offline mode:", tErr);
                    await saveOffline();
                }
            }

            alert("Authorization Complete! Logged as " + finalId);
            setItems({}); setSelectedDs(""); setShowPreview(false);
        } catch (err) { alert("Authorization Failed: " + err.message); }
        finally { setSubmitting(false); }
    };

    return (
        <div style={moduleWrap}>
            <div style={formCard}>
                <label style={label}>Personnel Assignment</label>
                <select style={select} value={selectedDs} onChange={e => setSelectedDs(e.target.value)}>
                    <option value="">-- Choose Salesman --</option>
                    {dsList.map(ds => <option key={ds.dsId} value={ds.dsId}>{ds.name}</option>)}
                </select>
                <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", background: "#f1f5f9", padding: "8px 12px", borderRadius: 10 }}>
                    <FaSearch color="#64748b" />
                    <input style={{ border: "none", background: "transparent", flex: 1, outline: "none", fontSize: 13, fontWeight: 600 }} placeholder="Quick search SKU..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
            </div>
            <div style={skuListScroll}>
                {skuList.filter(s => s.toLowerCase().includes(search.toLowerCase()) || skuMaster[s]?.Name?.toLowerCase().includes(search.toLowerCase())).map(sku => (
                    <div key={sku} style={skuRow}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 800 }}>{skuMaster[sku]?.Name}</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>@ ₹{Number(skuMaster[sku]?.dsBasicRate || 0).toFixed(2)}</div>
                        </div>
                        <input type="number" inputMode="numeric" style={qtyInput} placeholder="0" value={items[sku] || ""} onChange={e => setItems({ ...items, [sku]: Number(e.target.value) })} />
                    </div>
                ))}
            </div>
            <div style={footerAction}>
                <div style={{ color: "white" }}>
                    <div style={{ fontSize: 10, opacity: 0.6 }}>LOAD SUMMARY</div>
                    <div style={{ fontSize: 18, fontWeight: 900 }}>₹{totals.amt.toLocaleString()}</div>
                </div>
                <button style={primaryBtn} onClick={() => {
                    if (!selectedDs) return alert("Select DS Target");
                    if (totals.qty === 0) return alert("Enter Quantities");
                    setShowPreview(true);
                }}>AUTHORIZED DESPATCH</button>
            </div>

            {showPreview && (
                <div style={modalOverlay}>
                    <div style={modalCard}>
                        <div style={modalHeader}>
                            <h3 style={{ margin: 0 }}>Review Load Despatch</h3>
                            <button onClick={() => setShowPreview(false)} style={closeBtn}><FaTimes /></button>
                        </div>
                        <div style={modalBody}>
                            <div style={previewBox}>
                                <div style={prevLabel}>Target Personnel</div>
                                <div style={prevValue}>{dsList.find(d => d.dsId === selectedDs)?.name}</div>
                            </div>
                            <div style={prevList}>
                                {Object.entries(items).filter(([_, q]) => q > 0).map(([sku, q]) => (
                                    <div key={sku} style={prevRow}>
                                        <div style={{ flex: 1 }}>{skuMaster[sku]?.Name}</div>
                                        <div style={{ fontWeight: 800 }}>{q} units</div>
                                    </div>
                                ))}
                            </div>
                            <div style={prevSummary}>
                                <div>Total Units: {totals.qty}</div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: "var(--primary)" }}>₹{totals.amt.toLocaleString()}</div>
                            </div>
                        </div>
                        <div style={modalFooter}>
                            <button style={secondaryBtn} onClick={() => setShowPreview(false)}>Edit Load</button>
                            <button style={primaryBtn} onClick={handleConfirm} disabled={submitting}>
                                {submitting ? "Processing..." : "Confirm & Authorize"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* --- TAB 2: BILL TO OUTLET --- */
function BillOutletModule({ dsList, outletList, skuMaster, skuList, userProfile, companyInfo, isDesktop }) {
    const [selectedDs, setSelectedDs] = useState("");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [items, setItems] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState("");
    const [showPreview, setShowPreview] = useState(false);

    const filteredOutlets = useMemo(() => {
        if (!selectedDs) return [];
        return outletList.filter(o => o.dsId === selectedDs);
    }, [selectedDs, outletList]);

    const totals = useMemo(() => {
        let q = 0, a = 0, b = 0;
        Object.entries(items).forEach(([sku, qty]) => {
            if (qty > 0) {
                const m = skuMaster[sku];
                const rate = Number(m?.Basic_Eway || 0);
                const gst = Number(m?.gst || 5);
                const lb = qty * rate;
                const lt = lb * (1 + gst / 100);
                q += qty; b += lb; a += lt;
            }
        });
        return { q, a, b };
    }, [items, skuMaster]);

    const getCustomDocId = async (transaction, type) => {
        const todayYYMMDD = new Date().toISOString().slice(2, 10).replace(/-/g, ""); // "240317"
        const prefix = (companyInfo.name || "OU").slice(0, 2).toUpperCase();
        const counterId = `${userProfile.partyId}_${todayYYMMDD}_${type}`;
        const counterRef = doc(db, "counters", counterId);
        const counterSnap = await transaction.get(counterRef);
        let seq = 1;
        if (counterSnap.exists()) {
            seq = (counterSnap.data().seq || 0) + 1;
        }
        transaction.set(counterRef, { seq }, { merge: true });
        const serial = String(seq).padStart(4, "0");
        const displayId = `${prefix}/${todayYYMMDD}/${type}/${serial}`;
        const dbId = displayId.replace(/\//g, "-");
        return { dbId, displayId };
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            const outObj = outletList.find(o => o.outletId === selectedOutlet);
            const itemLines = buildItemLines(items, skuMaster);
            const todayStr = getIstDateStr();

            let finalId = "";
            const saveOffline = async () => {
                const tempId = Date.now().toString().slice(-6);
                finalId = `OFF-OU-${tempId}`;
                const dbId = finalId.replace(/\//g, "-");
                const orderRef = doc(db, "Outlet_Orders", dbId);
                const orderData = {
                    clientId: userProfile.clientId,
                    orderId: dbId,
                    displayOrderId: finalId,
                    distributorId: userProfile.partyId,
                    distributorName: companyInfo.name,
                    dsId: selectedDs || "DIRECT",
                    partyId: selectedOutlet,
                    partyName: outObj?.name || selectedOutlet,
                    outletName: outObj?.name || selectedOutlet,
                    beat: outObj?.beat || "DIRECT",
                    items: items,
                    itemLines: itemLines,
                    totalQty: totals.q,
                    totalAmount: totals.a,
                    totalBasic: totals.b,
                    cgst: (totals.a - totals.b) / 2,
                    sgst: (totals.a - totals.b) / 2,
                    source: "DISTRIBUTOR_MOBILE_DIRECT",
                    status: "PENDING_SYNC",
                    approvedAt: serverTimestamp(),
                    approvedDateOnly: todayStr,
                    createdAt: serverTimestamp(),
                };
                setDoc(orderRef, orderData);
                const distStockRef = doc(db, "distributor_stock_daily", `${userProfile.partyId}_${todayStr}`);
                setDoc(distStockRef, { distributorId: userProfile.partyId, date: todayStr }, { merge: true });
                const distUpdate = {};
                Object.entries(items).forEach(([sku, qty]) => { if (qty > 0) distUpdate[`inventory.${sku}.sold`] = increment(qty); });
                updateDoc(distStockRef, distUpdate);
            };

            if (!navigator.onLine) {
                await saveOffline();
            } else {
                try {
                    await Promise.race([
                        runTransaction(db, async (transaction) => {
                            const { dbId, displayId } = await getCustomDocId(transaction, "OU");
                            finalId = displayId;
                            const orderRef = doc(db, "Outlet_Orders", dbId);
                            const orderData = {
                                clientId: userProfile.clientId,
                                orderId: dbId,
                                displayOrderId: displayId,
                                distributorId: userProfile.partyId,
                                distributorName: companyInfo.name,
                                dsId: selectedDs || "DIRECT",
                                partyId: selectedOutlet,
                                partyName: outObj?.name || selectedOutlet,
                                outletName: outObj?.name || selectedOutlet,
                                beat: outObj?.beat || "DIRECT",
                                items: items,
                                itemLines: itemLines,
                                totalQty: totals.q,
                                totalAmount: totals.a,
                                totalBasic: totals.b,
                                cgst: (totals.a - totals.b) / 2,
                                sgst: (totals.a - totals.b) / 2,
                                source: "DISTRIBUTOR_MOBILE_DIRECT",
                                status: "APPROVED",
                                approvedAt: serverTimestamp(),
                                approvedDateOnly: todayStr,
                                createdAt: serverTimestamp(),
                            };
                            transaction.set(orderRef, orderData);
                            const distStockRef = doc(db, "distributor_stock_daily", `${userProfile.partyId}_${todayStr}`);
                            transaction.set(distStockRef, { distributorId: userProfile.partyId, date: todayStr }, { merge: true });
                            const distUpdate = {};
                            Object.entries(items).forEach(([sku, qty]) => { if (qty > 0) distUpdate[`inventory.${sku}.sold`] = increment(qty); });
                            transaction.update(distStockRef, distUpdate);
                            updateIncrementalAggregates(transaction, userProfile.partyId, totals.a, totals.q, selectedDs, selectedOutlet, sharedData.todayStock);
                        }),
                        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 2000))
                    ]);
                } catch (tErr) {
                    console.warn("Distributor Sync Slow, using offline mode:", tErr);
                    await saveOffline();
                }
            }

            alert("Direct Retail Bill Dispatched. ID: " + finalId);
            setItems({}); setSelectedOutlet(""); setShowPreview(false);
        } catch (err) { alert("Save Failed: " + err.message); }
        finally { setSubmitting(false); }
    };

    return (
        <div style={moduleWrap}>
            <div style={formCard}>
                <label style={label}>Route & Node</label>
                <div style={{ display: "flex", gap: 10 }}>
                    <select style={{ ...select, flex: 1 }} value={selectedDs} onChange={e => { setSelectedDs(e.target.value); setSelectedOutlet(""); }}>
                        <option value="">Executive</option>
                        {dsList.map(ds => <option key={ds.dsId} value={ds.dsId}>{ds.name}</option>)}
                    </select>
                    <select style={{ ...select, flex: 2 }} value={selectedOutlet} onChange={e => setSelectedOutlet(e.target.value)} disabled={!selectedDs}>
                        <option value="">-- Choose Outlet --</option>
                        {filteredOutlets.map(o => <option key={o.outletId} value={o.outletId}>{o.name}</option>)}
                    </select>
                </div>
                <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", background: "#f1f5f9", padding: "8px 12px", borderRadius: 10 }}>
                    <FaSearch color="#64748b" />
                    <input style={{ border: "none", background: "transparent", flex: 1, outline: "none", fontSize: 13, fontWeight: 600 }} placeholder="Search SKU..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
            </div>
            <div style={skuListScroll}>
                {skuList.filter(s => s.toLowerCase().includes(search.toLowerCase()) || skuMaster[s]?.Name?.toLowerCase().includes(search.toLowerCase())).map(sku => (
                    <div key={sku} style={skuRow}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 800 }}>{skuMaster[sku]?.Name}</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Retail: ₹{Number(skuMaster[sku]?.Basic_Eway || 0).toFixed(2)}</div>
                        </div>
                        <input type="number" inputMode="numeric" style={qtyInput} placeholder="0" value={items[sku] || ""} onChange={e => setItems({ ...items, [sku]: Number(e.target.value) })} />
                    </div>
                ))}
            </div>
            <div style={footerAction}>
                <div style={{ color: "white" }}>
                    <div style={{ fontSize: 10, opacity: 0.6 }}>DIRECT BILL VAL</div>
                    <div style={{ fontSize: 18, fontWeight: 900 }}>₹{totals.a.toLocaleString()}</div>
                </div>
                <button style={{ ...primaryBtn, background: "var(--success)" }} onClick={() => {
                   if (!selectedOutlet) return alert("Select Destination Outlet");
                   if (totals.q === 0) return alert("Enter Quantities");
                   setShowPreview(true);
                }}>BILL RETAIL</button>
            </div>

            {showPreview && (
                <div style={modalOverlay}>
                    <div style={modalCard}>
                        <div style={modalHeader}>
                            <h3 style={{ margin: 0 }}>Review Retail Bill</h3>
                            <button onClick={() => setShowPreview(false)} style={closeBtn}><FaTimes /></button>
                        </div>
                        <div style={modalBody}>
                            <div style={previewBox}>
                                <div style={prevLabel}>Destination Outlet</div>
                                <div style={prevValue}>{outletList.find(o => o.outletId === selectedOutlet)?.name}</div>
                            </div>
                            <div style={prevList}>
                                {Object.entries(items).filter(([_, q]) => q > 0).map(([sku, q]) => (
                                    <div key={sku} style={prevRow}>
                                        <div style={{ flex: 1 }}>{skuMaster[sku]?.Name}</div>
                                        <div style={{ fontWeight: 800 }}>{q} units</div>
                                    </div>
                                ))}
                            </div>
                            <div style={prevSummary}>
                                <div>Total Items: {totals.q}</div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: "var(--success)" }}>₹{totals.a.toLocaleString()}</div>
                            </div>
                        </div>
                        <div style={modalFooter}>
                            <button style={secondaryBtn} onClick={() => setShowPreview(false)}>Edit Items</button>
                            <button style={{ ...primaryBtn, background: "var(--success)" }} onClick={handleConfirm} disabled={submitting}>
                                {submitting ? "Syncing..." : "Confirm & Generate Bill"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* --- TAB 3: MASTER MODULE --- */
function MasterModule({ dsList, outletList, userProfile, companyInfo, isDesktop }) {
    const [view, setView] = useState("ds");
    const [search, setSearch] = useState("");
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [password, setPassword] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [activeEditItem, setActiveEditItem] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'add' | 'edit'

    const data = view === "ds" ? dsList : outletList;
    const filtered = data.filter(d => (d.name || "").toLowerCase().includes(search.toLowerCase()) || (d.dsId || d.outletId || "").toLowerCase().includes(search.toLowerCase()));

    const checkPassword = () => {
        const correct = getMasterKey();
        if (password === correct) {
            setIsAdmin(true);
            setShowPasswordModal(false);
            if (pendingAction === "add") setShowFormModal(true);
            else if (pendingAction === "edit") setShowFormModal(true);
        } else {
            alert("Lock breach attempt failed. Incorrect Sync Key.");
        }
        setPassword("");
    };


    const handleAddClick = () => {
        setPendingAction("add");
        setActiveEditItem(null);
        setShowFormModal(true);
    };


    const handleEditClick = (item) => {
        setPendingAction("edit");
        setActiveEditItem(item);
        if (!isAdmin) setShowPasswordModal(true);
        else setShowFormModal(true);
    };

    return (
        <div style={moduleWrap}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 20px 10px" }}>
                <div style={{ display: "flex", gap: 8 }}>
                    <button style={view === "ds" ? activePill : pill} onClick={() => setView("ds")}>DS PERSONNEL</button>
                    <button style={view === "outlet" ? activePill : pill} onClick={() => setView("outlet")}>RETAIL NODES</button>
                </div>
                <button style={{ ...pill, background: "var(--primary)", color: "white", borderColor: "var(--primary)", display: "flex", alignItems: "center", gap: 6 }} onClick={handleAddClick}>
                    <FaPlus /> ADD
                </button>
            </div>
            <div style={{ padding: "10px 20px" }}>
                <div style={searchWrap}><FaSearch color="#64748b" /> <input style={searchInp} placeholder={`Search ${view.toUpperCase()} Registry...`} value={search} onChange={e => setSearch(e.target.value)} /></div>
            </div>
            <div style={skuListScroll}>
                {filtered.map(item => (
                    <div key={item.id} style={skuRow}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 900, color: "#1e293b" }}>{item.name}</div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>ID: {item.dsId || item.outletId} | {item.phone || item.route || item.beat || "Ungated Territory"}</div>
                        </div>
                        <button style={iconBtn} onClick={() => handleEditClick(item)}><FaEdit size={18} /></button>
                    </div>
                ))}
            </div>

            {showPasswordModal && (
                <div style={modalOverlay}>
                    <div style={modalCard}>
                        <div style={modalHeader}>
                            <h3>Master Synchronization Access</h3>
                            <button onClick={() => setShowPasswordModal(false)} style={closeBtn}><FaTimes /></button>
                        </div>
                        <div style={{ padding: "20px 0" }}>
                            <label style={label}>Daily Sync Key (Request from C&F)</label>
                            <input type="password" inputMode="numeric" style={{ ...select, textAlign: "center", fontSize: 24, letterSpacing: 10 }} value={password} onChange={e => setPassword(e.target.value)} autoFocus placeholder="****" maxLength={4} />
                        </div>
                        <button style={{ ...primaryBtn, width: "100%" }} onClick={checkPassword}>VERIFY ACCESS</button>
                    </div>
                </div>
            )}

            {showFormModal && (
                view === "ds" ? (
                    <DsFormModal 
                        item={activeEditItem} 
                        onClose={() => setShowFormModal(false)} 
                        userProfile={userProfile} 
                        companyInfo={companyInfo} 
                        isEditing={pendingAction === "edit"} 
                    />
                ) : (
                    <OutletFormModal 
                        item={activeEditItem} 
                        dsList={dsList}
                        onClose={() => setShowFormModal(false)} 
                        userProfile={userProfile} 
                        companyInfo={companyInfo} 
                        isEditing={pendingAction === "edit"} 
                    />
                )
            )}

        </div>
    );
}

/* --- DS FORM MODAL --- */
function DsFormModal({ item, onClose, userProfile, companyInfo, isEditing }) {
    const [form, setForm] = useState(item || {
        dsId: "", name: "", phone: "", route: "", vehicleNo: "",
        monArea: "", tueArea: "", wedArea: "", thuArea: "", friArea: "", satArea: ""
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                distributorId: userProfile.partyId,
                distributorName: companyInfo.name,
                clientId: userProfile.clientId,
                status: "active"
            };
            if (isEditing) {
                await updateDoc(doc(db, "dsMaster", item.id), payload);
            } else {
                await setDoc(doc(db, "dsMaster", form.dsId), payload);
            }
            alert("Salesman Record Synchronized.");
            window.location.reload(); 
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    return (
        <div style={modalOverlay}>
            <div style={{ ...modalCard, maxHeight: "90vh", overflowY: "auto" }}>
                <div style={modalHeader}>
                    <h3>{isEditing ? "Modify Executive" : "Register Executive"}</h3>
                    <button onClick={onClose} style={closeBtn}><FaTimes /></button>
                </div>
                <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <label style={label}>Executive ID</label>
                    <input disabled={isEditing} style={select} value={form.dsId} onChange={e => setForm({...form, dsId: e.target.value})} required placeholder="DS-XXX" />
                    
                    <label style={label}>Full Name</label>
                    <input style={select} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                    
                    <label style={label}>Phone</label>
                    <input style={select} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />

                    <div style={{ display: "flex", gap: 10 }}>
                        <div style={{ flex: 1 }}><label style={label}>Route</label><input style={select} value={form.route} onChange={e => setForm({...form, route: e.target.value})} /></div>
                        <div style={{ flex: 1 }}><label style={label}>Vehicle</label><input style={select} value={form.vehicleNo} onChange={e => setForm({...form, vehicleNo: e.target.value})} /></div>
                    </div>

                    <div style={{ background: "#f8fafc", padding: 12, borderRadius: 12 }}>
                        <div style={label}>Beat Matrix</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <input style={{...select, padding: 8}} placeholder="Mon Area" value={form.monArea} onChange={e => setForm({...form, monArea: e.target.value})} />
                            <input style={{...select, padding: 8}} placeholder="Tue Area" value={form.tueArea} onChange={e => setForm({...form, tueArea: e.target.value})} />
                            <input style={{...select, padding: 8}} placeholder="Wed Area" value={form.wedArea} onChange={e => setForm({...form, wedArea: e.target.value})} />
                            <input style={{...select, padding: 8}} placeholder="Thu Area" value={form.thuArea} onChange={e => setForm({...form, thuArea: e.target.value})} />
                            <input style={{...select, padding: 8}} placeholder="Fri Area" value={form.friArea} onChange={e => setForm({...form, friArea: e.target.value})} />
                            <input style={{...select, padding: 8}} placeholder="Sat Area" value={form.satArea} onChange={e => setForm({...form, satArea: e.target.value})} />
                        </div>
                    </div>

                    <button type="submit" style={primaryBtn} disabled={saving}>{saving ? "Syncing..." : "COMMIT RECORD"}</button>
                </form>
            </div>
        </div>
    );
}

/* --- OUTLET FORM MODAL --- */
function OutletFormModal({ item, dsList, onClose, userProfile, companyInfo, isEditing }) {
    const [form, setForm] = useState(item || {
        outletId: "", name: "", phone: "", dsId: "", beat: "MON", beatName: "", address: ""
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const selDs = dsList.find(d => d.dsId === form.dsId);
            const payload = {
                ...form,
                dsName: selDs?.name || "",
                distributorId: userProfile.partyId,
                clientId: userProfile.clientId,
                status: "active"
            };
            if (isEditing) {
                await updateDoc(doc(db, "outletMaster", item.id), payload);
            } else {
                await setDoc(doc(db, "outletMaster", form.outletId), payload);
            }
            alert("Retail Node Synchronized.");
            window.location.reload();
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    return (
        <div style={modalOverlay}>
            <div style={{ ...modalCard, maxHeight: "90vh", overflowY: "auto" }}>
                <div style={modalHeader}>
                    <h3>{isEditing ? "Modify Retail Node" : "Affiliate Retail Node"}</h3>
                    <button onClick={onClose} style={closeBtn}><FaTimes /></button>
                </div>
                <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <label style={label}>Outlet ID</label>
                    <input disabled={isEditing} style={select} value={form.outletId} onChange={e => setForm({...form, outletId: e.target.value})} required placeholder="OU-XXX" />
                    
                    <label style={label}>Shop Name</label>
                    <input style={select} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                    
                    <label style={label}>Phone</label>
                    <input style={select} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />

                    <label style={label}>Assigned Executive</label>
                    <select style={select} value={form.dsId} onChange={e => setForm({...form, dsId: e.target.value})} required>
                        <option value="">-- Choose DS --</option>
                        {dsList.map(ds => <option key={ds.dsId} value={ds.dsId}>{ds.name}</option>)}
                    </select>

                    <div style={{ display: "flex", gap: 10 }}>
                        <div style={{ flex: 1 }}>
                            <label style={label}>Operation Day</label>
                            <select style={select} value={form.beat} onChange={e => setForm({...form, beat: e.target.value})}>
                                {["MON", "TUE", "WED", "THU", "FRI", "SAT"].map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                        <div style={{ flex: 2 }}>
                            <label style={label}>Beat Name</label>
                            <input style={select} value={form.beatName} onChange={e => setForm({...form, beatName: e.target.value})} placeholder="Main Bazar, etc." />
                        </div>
                    </div>

                    <label style={label}>Full Address</label>
                    <textarea style={{...select, height: 80}} value={form.address} onChange={e => setForm({...form, address: e.target.value})} />

                    <button type="submit" style={{...primaryBtn, background: "var(--success)"}} disabled={saving}>{saving ? "Syncing..." : "COMMIT NODE"}</button>
                </form>
            </div>
        </div>
    );
}

/* --- TAB 4: STOCK LEDGER --- */
function StockLedgerModule({ userProfile, skuMaster, skuList, sharedData, isDesktop }) {
    const inventory = sharedData.todayStock?.inventory || {};

    return (
        <div style={moduleWrap}>
            <div style={{ padding: 24, background: "white", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h2 style={{ fontSize: 20, fontWeight: 900, margin: 0, letterSpacing: -0.5 }}>Warehouse Reservoir</h2>
                    <div style={{ padding: "4px 10px", borderRadius: 20, background: "rgba(16, 185, 129, 0.1)", color: "#10b981", fontSize: 10, fontWeight: 800 }}>LIVE SYNC</div>
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Inventory audit for {getIstDateStr()}</div>
            </div>
            <div style={skuListScroll}>
                {skuList.map(sku => {
                    const info = inventory[sku] || { opening: 0, received: 0, sold: 0 };
                    const closing = (info.opening || 0) + (info.received || 0) - (info.sold || 0);
                    return (
                        <div key={sku} style={skuRow}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, color: "#1e293b" }}>{skuMaster[sku]?.Name}</div>
                                <div style={{ fontSize: 13, color: "#64748b", display: "flex", gap: 12, marginTop: 6, fontWeight: 600 }}>
                                    <span>OP: <span style={{ color: "#1e293b" }}>{info.opening}</span></span>
                                    <span>RCV: <span style={{ color: "#1e293b" }}>{info.received}</span></span>
                                    <span>SLD: <span style={{ color: "#ef4444" }}>{info.sold}</span></span>
                                </div>

                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div style={{ fontWeight: 900, color: closing > 0 ? "#10b981" : "#ef4444", fontSize: 22 }}>{closing}</div>
                                <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700 }}>AVAILABLE</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* --- TAB 5: SALES REPORTS --- */
function SalesReportModule({ userProfile, sharedData, isDesktop }) {
    const [reportType, setReportType] = useState("ds"); // 'ds', 'outlet', or 'summary'
    const [selectedDate, setSelectedDate] = useState(getIstDateStr());

    const last3Days = useMemo(() => {
        return [0, 1, 2].map(d => {
            const date = new Date();
            date.setDate(date.getDate() - d);
            return new Intl.DateTimeFormat('en-CA', {
                timeZone: 'Asia/Kolkata',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).format(date);
        });
    }, []);

    // Filter shared logs for daily view
    const logs = useMemo(() => {
        if (reportType === "summary") return [];
        const source = reportType === "ds" ? sharedData.dsOrders : sharedData.outletOrders;
        // restricted to 3 days in parent for unified caching
        return source.filter(log => {
            if (!log.createdAt) return false;
            const logDate = new Intl.DateTimeFormat('en-CA', {
                timeZone: 'Asia/Kolkata',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).format(log.createdAt.toDate());
            return logDate === selectedDate;
        }).sort((a,b) => b.createdAt.toDate() - a.createdAt.toDate());
    }, [sharedData, reportType, selectedDate]);

    const summaries = useMemo(() => {
        if (reportType !== "summary") return null;
        
        const dateMap = {};
        const dsMap = {};
        const outletMap = {};

        // Restrict to the 3-day buffer to reuse the same light-weight pull
        [...sharedData.dsOrders, ...sharedData.outletOrders].forEach(order => {
            const date = order.createdAt?.toDate().toLocaleDateString('en-GB') || "N/A";
            const amt = Number(order.totalAmount || 0);
            dateMap[date] = (dateMap[date] || 0) + amt;

            if (order.source === "DISTRIBUTOR_TO_DS") {
                const ds = order.partyName || "UNKNOWN";
                dsMap[ds] = (dsMap[ds] || 0) + amt;
            } else {
                const outlet = order.partyName || order.outletName || "UNKNOWN";
                outletMap[outlet] = (outletMap[outlet] || 0) + amt;
            }
        });

        return {
            dateWise: Object.entries(dateMap).sort((a,b) => {
                const da = a[0].split('/').reverse().join('');
                const db = b[0].split('/').reverse().join('');
                return db.localeCompare(da);
            }),
            dsWise: Object.entries(dsMap).sort((a,b) => b[1] - a[1]),
            outletWise: Object.entries(outletMap).sort((a,b) => b[1] - a[1]).slice(0, 50)
        };
    }, [sharedData, reportType]);

    return (
        <div style={moduleWrap}>
            <div style={{ padding: "20px 24px", background: "white", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0 }}>Business Intelligence</h2>
                    <div style={{ display: "flex", gap: 4 }}>
                        <button style={reportType === "ds" ? miniActivePill : miniPill} onClick={() => setReportType("ds")}>DS</button>
                        <button style={reportType === "outlet" ? miniActivePill : miniPill} onClick={() => setReportType("outlet")}>RETAIL</button>
                        <button style={reportType === "summary" ? miniActivePill : miniPill} onClick={() => setReportType("summary")}>M-SUMM</button>
                    </div>
                </div>
                
                {reportType !== "summary" ? (
                    <div style={{ display: "flex", gap: 10 }}>
                        {last3Days.map(date => (
                            <button key={date} style={selectedDate === date ? dateActiveBtn : dateBtn} onClick={() => setSelectedDate(date)}>
                                {date === getIstDateStr() ? "TODAY" : date.split("-").slice(1).reverse().join("/")}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div style={{ fontSize: 12, fontWeight: 800, color: "var(--primary)" }}>📊 CURRENT MONTH AGGREGATED SALES</div>
                )}
            </div>

            <div style={skuListScroll}>
                {reportType === "summary" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 40 }}>
                        <div style={{ padding: "10px 16px", background: "#fef3c7", color: "#92400e", fontSize: 11, fontWeight: 700, borderRadius: 8, margin: "0 16px" }}>Showing Recent 3-Day Shared Analytics</div>
                        {/* Date Wise */}
                        <section>
                            <h3 style={sumHeader}>1. RECENT DATE-WISE PERFORMANCE</h3>
                            <div style={sumBox}>
                                {summaries.dateWise.map(([d, a]) => (
                                    <div key={d} style={sumRow}><span>{d}</span><span style={{ fontWeight: 900 }}>₹{a.toLocaleString()}</span></div>
                                ))}
                            </div>
                        </section>
                        {/* DS Wise */}
                        <section>
                            <h3 style={sumHeader}>2. EXECUTIVE-WISE CONTRIBUTION</h3>
                            <div style={sumBox}>
                                {summaries.dsWise.map(([ds, a]) => (
                                    <div key={ds} style={sumRow}><span>{ds}</span><span style={{ fontWeight: 900 }}>₹{a.toLocaleString()}</span></div>
                                ))}
                            </div>
                        </section>
                        {/* Outlet Wise */}
                        <section>
                            <h3 style={sumHeader}>3. RETAIL NODE ANALYSIS</h3>
                            <div style={sumBox}>
                                {summaries.outletWise.map(([out, a]) => (
                                    <div key={out} style={sumRow}><span>{out}</span><span style={{ fontWeight: 900 }}>₹{a.toLocaleString()}</span></div>
                                ))}
                            </div>
                        </section>
                    </div>
                ) : (
                    logs.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
                            <FaClipboardList size={40} style={{ opacity: 0.2, marginBottom: 12 }} />
                            <div style={{ fontWeight: 700 }}>No Activity Found</div>
                        </div>
                    ) : logs.map(log => (
                        <div key={log.id} style={{ ...skuRow, flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                <div style={{ fontWeight: 900, color: "#1e293b", fontSize: 15 }}>{log.partyName || log.outletName}</div>
                                <div style={{ fontWeight: 900, color: reportType === "ds" ? "var(--primary)" : "var(--success)" }}>
                                    ₹{log.totalAmount?.toLocaleString()}
                                </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", fontSize: 11, color: "#64748b" }}>
                                <span>Qty: {log.totalQty} Units • {log.status}</span>
                                <span>{log.createdAt?.toDate()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", borderTop: "1px solid #f8fafc", paddingTop: 4 }}>
                                <div style={{ fontSize: 9, color: "var(--primary)", fontWeight: 800 }}>REF # {log.displayOrderId || log.orderId || log.id}</div>
                                {reportType === "outlet" && <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700 }}>BEAT: {log.beat || "DIRECT"}</div>}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

// Additional Summary Styles
/* --- TAB 0: EXECUTIVE COCKPIT --- */
function CockpitModule({ dsList, skuList, userProfile, sharedData, isDesktop }) {
    const stats = useMemo(() => {
        const todayStr = getIstDateStr();
        const agg = sharedData.aggregate || {};
        const daily = agg.daily?.[todayStr] || {};

        // 1. Stock Metrics (Optimized: prefer pre-calculated record for instant single-line load)
        const aggOp = Number(agg.mtdStockOpeningUnits) || 0;
        const aggRcv = Number(agg.mtdStockReceivedUnits) || 0;
        const aggSld = Number(agg.mtdStockSoldUnits) || 0;

        let op = aggOp, pur = aggRcv, cur = aggOp + aggRcv - aggSld;

        // Fallback: If aggregator is new/empty, use the stock map if available (or zero)
        if (aggOp === 0 && sharedData.todayStock) {
            const inv = sharedData.todayStock.inventory || {};
            op = 0; pur = 0; cur = 0;
            Object.values(inv).forEach(item => {
                op += (item.opening || 0);
                pur += (item.received || 0);
                cur += (item.opening || 0) + (item.received || 0) - (item.sold || 0);
            });
        }

        // 2. MTD Sales & Unique Outlets (Single agg record)
        const mtd = agg.mtdSalesAmount || 0;
        const mtdUnits = agg.mtdSalesUnits || 0;
        const todayS = daily.salesAmount || 0;
        const monthUniqueO = Object.keys(agg.uniqueOutlets || {}).length;

        // 3. DS Performance (Single agg record)
        const dsBilledToday = Object.keys(daily.dsBilled || {}).length;
        const todayUnits = daily.units || 0;

        return {
            opening: op,
            purchase: pur, 
            mtdSales: mtd,
            mtdSalesUnits: mtdUnits,
            currentStock: cur,
            todaySales: todayS,
            todayDsBilled: dsBilledToday,
            todayAvgQty: dsBilledToday > 0 ? (todayUnits / dsBilledToday).toFixed(1) : 0,
            monthAvgQty: (mtdUnits / (dsList.length || 1) / 30).toFixed(1),
            monthUniqueOutlets: monthUniqueO
        };
    }, [sharedData, dsList]);

    if (sharedData.isSyncing) return <div style={{ padding: 40, textAlign: "center", fontWeight: 800, color: "var(--primary)" }}>Syncing Strategist...</div>;

    return (
        <div style={moduleWrap}>
            <div style={{ padding: 24, background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)", color: "white", borderRadius: isDesktop ? "16px 16px 0 0" : "0" }}>
                <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.8, letterSpacing: 2, color: "white" }}>EXECUTIVE COCKPIT</div>
                <h1 style={{ fontSize: 28, fontWeight: 900, marginTop: 4, letterSpacing: -1, color: "white" }}>Strategic Hub</h1>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: isDesktop ? "0" : "20px 16px" }}>
                {/* Section 1: MTD Inventory Status */}
                <h3 style={sumHeader}>MTD Inventory Status</h3>
                <div style={{ ...cockpitGrid, gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr" : "1fr 1fr" }}>
                    <div style={statCard}>
                        <div style={statLabel}>Month Opening Qty</div>
                        <div style={statVal}>{stats.opening}</div>
                    </div>
                    <div style={statCard}>
                        <div style={statLabel}>Purchase MTD Qty</div>
                        <div style={statVal}>{stats.purchase}</div>
                    </div>
                    <div style={statCard}>
                        <div style={statLabel}>Sales MTD Qty</div>
                        <div style={statVal}>{stats.mtdSalesUnits}</div>
                    </div>
                    <div style={statCard}>
                        <div style={statLabel}>Live Stock Qty</div>
                        <div style={{ ...statVal, color: stats.currentStock < 100 ? "#ef4444" : "#10b981" }}>{stats.currentStock}</div>
                    </div>
                </div>

                {/* Section 2: Execution Summary */}
                <h3 style={sumHeader}>Execution Summary</h3>
                <div style={{ ...cockpitGrid, gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr 1fr" : "1fr 1fr" }}>
                    <div style={{ ...statCard, background: "var(--primary)", color: "white" }}>
                        <div style={{ ...statLabel, color: "rgba(255,255,255,0.7)" }}>Today's Sales Value</div>
                        <div style={statVal}>₹{stats.todaySales.toLocaleString()}</div>
                    </div>
                    <div style={{ ...statCard, background: "#f97316", color: "white" }}>
                        <div style={{ ...statLabel, color: "rgba(255,255,255,0.7)" }}>MTD Sales Value</div>
                        <div style={statVal}>₹{stats.mtdSales.toLocaleString()}</div>
                    </div>
                    <div style={statCard}>
                        <div style={statLabel}>Today's Billed DS</div>
                        <div style={statVal}>{stats.todayDsBilled} {dsList.length > 0 && <span style={{ fontSize: 12, opacity: 0.5 }}>/ {dsList.length}</span>}</div>
                    </div>
                    <div style={statCard}>
                        <div style={statLabel}>Avg Qty/DS (Today)</div>
                        <div style={statVal}>{stats.todayAvgQty}</div>
                    </div>
                    <div style={statCard}>
                        <div style={statLabel}>Avg Qty/DS (Month)</div>
                        <div style={statVal}>{stats.monthAvgQty}</div>
                    </div>
                </div>

                {/* Section 3: Retail Pulse */}
                <h3 style={sumHeader}>Retail Pulse</h3>
                <div style={{ ...cockpitGrid, gridTemplateColumns: isDesktop ? "1fr 1fr 1fr" : "1fr 1fr" }}>
                    <div style={statCard}>
                        <div style={statLabel}>Unique Monthly Reach</div>
                        <div style={statVal}>{stats.monthUniqueOutlets}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const cockpitGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 };
const statCard = { background: "white", padding: 16, borderRadius: 20, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #f1f5f9" };
const statLabel = { fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 6 };
const statVal = { fontSize: 18, fontWeight: 900, color: "#0f172a" };

const miniPill = { padding: "6px 10px", borderRadius: 8, border: "1px solid #f1f5f9", background: "white", fontSize: 10, fontWeight: 800, color: "#64748b" };

const miniActivePill = { ...miniPill, background: "#0f172a", color: "white", borderColor: "#0f172a" };
const dateBtn = { flex: 1, padding: "10px 5px", borderRadius: 10, border: "none", background: "#f1f5f9", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "pointer" };
const dateActiveBtn = { ...dateBtn, background: "#0f172a", color: "white" };
const sumHeader = { fontSize: 11, fontWeight: 900, color: "#94a3b8", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 };
const sumBox = { background: "white", borderRadius: 16, border: "1px solid #f1f5f9", padding: "8px 0" };
const sumRow = { display: "flex", justifyContent: "space-between", padding: "12px 20px", borderBottom: "1px solid #f8fafc", fontSize: 13, color: "#1e293b" };



// STYLES
const container = { maxWidth: "500px", margin: "0 auto", background: "#f8fafc", height: "100vh", position: "relative", display: "flex", flexDirection: "column", fontFamily: "'Inter', sans-serif" };
const header = { padding: "40px 24px 20px", background: "linear-gradient(135deg, #0f172a, #1e293b)", display: "flex", justifyContent: "space-between", alignItems: "center" };
const pulse = { width: 8, height: 8, background: "#10b981", borderRadius: "50%", boxShadow: "0 0 10px #10b981" };
const content = { flex: 1, overflow: "hidden" };
const bottomNav = { height: 84, background: "white", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-around", alignItems: "center", paddingBottom: 20 };
const navItem = (active) => ({ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, fontSize: 18, color: active ? "var(--primary)" : "#94a3b8", cursor: "pointer", transition: "0.2s", fontWeight: active ? 800 : 500 });
const moduleWrap = { height: "100%", display: "flex", flexDirection: "column" };
const formCard = { padding: "20px 24px", background: "white", borderBottom: "1px solid #f1f5f9" };
const select = { width: "100%", padding: 14, borderRadius: 12, border: "2px solid #f1f5f9", fontSize: 14, background: "#f8fafc", fontWeight: 700, outline: "none" };
const label = { fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 8, display: "block", letterSpacing: 0.5 };
const skuListScroll = { flex: 1, overflowY: "auto", padding: "10px 16px" };
const skuRow = { padding: "16px 20px", background: "white", borderRadius: 16, border: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12, marginBottom: 10, boxShadow: "0 2px 4px rgba(0,0,0,0.02)" };
const qtyInput = { width: 70, padding: "10px 5px", borderRadius: 10, border: "2px solid #e2e8f0", textAlign: "center", fontWeight: 900, fontSize: 16, outline: "none" };
const footerAction = { padding: "24px 24px 32px", background: "#0f172a", display: "flex", justifyContent: "space-between", alignItems: "center", borderTopLeftRadius: 24, borderTopRightRadius: 24 };
const primaryBtn = { padding: "14px 24px", borderRadius: 12, background: "var(--primary)", color: "white", border: "none", fontWeight: 900, fontSize: 13, textTransform: "uppercase", letterSpacing: 1, boxShadow: "0 4px 14px 0 rgba(0, 118, 255, 0.39)" };
const secondaryBtn = { padding: "14px 24px", borderRadius: 12, background: "#f1f5f9", color: "#1e293b", border: "none", fontWeight: 800, fontSize: 13 };
const loaderStyle = { height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "#f8fafc", fontWeight: 900, color: "var(--primary)", fontSize: 14 };
const pill = { padding: "8px 16px", borderRadius: 20, border: "2px solid #f1f5f9", background: "white", fontSize: 11, fontWeight: 800, color: "#64748b" };
const activePill = { ...pill, background: "#0f172a", color: "white", borderColor: "#0f172a" };
const searchWrap = { display: "flex", alignItems: "center", gap: 10, padding: 12, background: "#f1f5f9", borderRadius: 12 };
const searchInp = { border: "none", background: "transparent", flex: 1, outline: "none", fontWeight: 700, fontSize: 14 };
const logoutBtn = { width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.1)", border: "none", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 };

const modalOverlay = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end", zIndex: 1000, backdropFilter: "blur(4px)" };
const modalCard = { width: "100%", maxWidth: "500px", background: "white", borderTopLeftRadius: 32, borderTopRightRadius: 32, padding: "24px", animation: "slideUp 0.3s ease-out" };
const modalHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 };
const closeBtn = { background: "#f1f5f9", border: "none", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };
const modalBody = { maxHeight: "60vh", overflowY: "auto", marginBottom: 20 };
const modalFooter = { display: "flex", gap: 12 };
const previewBox = { background: "#f8fafc", padding: 16, borderRadius: 16, marginBottom: 16, border: "1px solid #f1f5f9" };
const prevLabel = { fontSize: 10, color: "#94a3b8", fontWeight: 800, textTransform: "uppercase", marginBottom: 4 };
const prevValue = { fontSize: 18, fontWeight: 900, color: "#1e293b" };
const prevList = { display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 };
const prevRow = { display: "flex", justifyContent: "space-between", fontSize: 14, color: "#475569", background: "#fff", padding: "12px 16px", borderRadius: 12, border: "1px solid #f1f5f9" };
const prevSummary = { borderTop: "2px dashed #f1f5f9", paddingTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 800 };
const iconBtn = { background: "transparent", border: "none", color: "var(--primary)", padding: 8, cursor: "pointer" };

export default DistributorMobileDashboard;

/* --- ADAPTIVE DESKTOP VIEW --- */
function DistributorDesktopView({ activeTab, setActiveTab, userProfile, companyInfo, sharedData, skuMaster, skuList, dsList, outletList, handleLogout, isDesktop }) {
    return (
        <BlankLayout>
            <div style={{ display: "flex", height: "100vh", background: "#f1f5f9", fontFamily: "'Inter', sans-serif" }}>
                {/* Desk Side Nav */}
                <div style={{ width: 280, background: "#0f172a", color: "white", display: "flex", flexDirection: "column", padding: "30px 0" }}>
                    <div style={{ padding: "0 30px", marginBottom: 50 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ ...pulse, width: 12, height: 12 }}></div>
                            <h2 style={{ fontSize: 20, fontWeight: 900, letterSpacing: -1 }}>QUICKBILL</h2>
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.4, marginTop: 4, textTransform: "uppercase" }}>Control Tower v2.0</div>
                    </div>

                    <div style={{ flex: 1 }}>
                        {[
                            { id: "cockpit", label: "Strategic Hub", icon: <FaHistory /> },
                            { id: "bill_ds", label: "Bill to DS", icon: <FaTruckLoading /> },
                            { id: "bill_outlet", label: "Bill to Retail", icon: <FaStore /> },
                            { id: "master", label: "Master Suite", icon: <FaDatabase /> },
                            { id: "stock", label: "Inventory Ledger", icon: <FaBoxes /> },
                            { id: "report", label: "Intelligence Reports", icon: <FaClipboardList /> }
                        ].map(m => (
                            <div 
                                key={m.id} 
                                onClick={() => setActiveTab(m.id)}
                                style={{ 
                                    padding: "16px 30px", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    gap: 16, 
                                    cursor: "pointer",
                                    fontSize: 15,
                                    fontWeight: activeTab === m.id ? 800 : 500,
                                    background: activeTab === m.id ? "rgba(255,255,255,0.05)" : "transparent",
                                    borderLeft: `4px solid ${activeTab === m.id ? "#3b82f6" : "transparent"}`,
                                    transition: "0.2s"
                                }}
                            >
                                <span style={{ fontSize: 20, opacity: activeTab === m.id ? 1 : 0.5 }}>{m.icon}</span>
                                <span>{m.label}</span>
                            </div>
                        ))}
                    </div>

                    <div style={{ padding: "0 30px" }}>
                        <div style={{ background: "rgba(255,255,255,0.03)", padding: 20, borderRadius: 16, marginBottom: 20 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.5 }}>LOGGED AS</div>
                            <div style={{ fontWeight: 800, fontSize: 14, marginTop: 4 }}>{companyInfo.name}</div>
                        </div>
                        <button 
                            onClick={handleLogout}
                            style={{ width: "100%", padding: 14, borderRadius: 12, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "white", fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
                        >
                            <FaSignOutAlt /> EXIT TERMINAL
                        </button>
                    </div>
                </div>

                {/* Desk Main Content */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {/* Top Bar */}
                    <div style={{ height: 80, background: "white", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 40px" }}>
                        <h2 style={{ fontSize: 20, fontWeight: 900, color: "#1e293b" }}>{companyInfo.name} <span style={{ color: "#94a3b8", fontWeight: 400 }}>/ Control Tower</span></h2>
                        <div style={{ display: "flex", gap: 20 }}>
                           <div style={{ textAlign: "right" }}>
                               <div style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8" }}>CONNECTION</div>
                               <div style={{ fontSize: 14, fontWeight: 900, color: "#10b981" }}>LIVE HUB</div>
                           </div>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflowY: "auto", padding: 40, background: "#f8fafc" }}>
                        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                            {activeTab === "cockpit" && <CockpitModule dsList={dsList} skuList={skuList} userProfile={userProfile} sharedData={sharedData} isDesktop={isDesktop} />}
                            {activeTab === "bill_ds" && <BillDsModule dsList={dsList} skuMaster={skuMaster} skuList={skuList} userProfile={userProfile} companyInfo={companyInfo} isDesktop={isDesktop} />}
                            {activeTab === "bill_outlet" && <BillOutletModule dsList={dsList} outletList={outletList} skuMaster={skuMaster} skuList={skuList} userProfile={userProfile} companyInfo={companyInfo} isDesktop={isDesktop} />}
                            {activeTab === "master" && <MasterModule dsList={dsList} outletList={outletList} userProfile={userProfile} companyInfo={companyInfo} isDesktop={isDesktop} />}
                            {activeTab === "stock" && <StockLedgerModule userProfile={userProfile} skuMaster={skuMaster} skuList={skuList} sharedData={sharedData} isDesktop={isDesktop} />}
                            {activeTab === "report" && <SalesReportModule userProfile={userProfile} sharedData={sharedData} isDesktop={isDesktop} />}
                        </div>
                    </div>
                </div>
            </div>
        </BlankLayout>
    );
}
